import { useTranslation } from 'react-i18next';
import { HolographicCard } from './HolographicCard';
import { useCards, useCardBalance, useAllCardBalances } from '../../hooks/useCards';
import { usePendingRecharges } from '../../hooks/useRechargeSettlements';
import { isMastercardTemplate, sortCardsByBalance, type Card } from '../../lib/cards';
import { formatUsd } from '../../lib/format';
import { HIDDEN_BALANCE, useUiStore } from '../../store/ui';

function CardPreview({ card, crediting }: { card: Card; crediting?: boolean }) {
  const { t } = useTranslation();
  const { data } = useCardBalance(card.id, card.status !== 'pending');
  const balanceHidden = useUiStore((s) => s.balanceHidden);
  const bal = data?.balance?.balance;
  const balance = balanceHidden
    ? HIDDEN_BALANCE
    : card.status === 'pending'
      ? t('cards.pending')
      : crediting
        ? t('cards.topUpCrediting')
        : bal != null
          ? `$${bal.toFixed(2)}`
          : '$0.00';
  return (
    <HolographicCard
      balance={balance}
      last4={card.last4 ?? undefined}
      label={card.label?.trim() || card.name}
      mastercard={isMastercardTemplate(card.templateId)}
      frozen={card.status === 'frozen'}
    />
  );
}

/** Home-screen preview of the user's live cards + dashed add tile. Hidden when none. */
export function CardsCarousel({
  onCardClick,
  onAddCard,
}: {
  onCardClick?: (cardId: string) => void;
  onAddCard?: () => void;
}) {
  const { t } = useTranslation();
  const { cards } = useCards();
  const balanceMap = useAllCardBalances(cards);
  const balanceHidden = useUiStore((s) => s.balanceHidden);
  const pendingByCard = usePendingRecharges();
  if (cards.length === 0) return null;

  // Match headline total: skip pending + frozen (funds locked until unfrozen).
  const cardsUsd = cards.reduce((sum, c) => {
    if (c.status === 'pending' || c.status === 'frozen') return sum;
    return sum + (balanceMap[c.id] || 0);
  }, 0);
  const headerBalance = balanceHidden ? HIDDEN_BALANCE : `$${formatUsd(cardsUsd)}`;

  const sorted = sortCardsByBalance(cards, balanceMap);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[16px] font-medium text-ink">{t('wallet.cards')}</h2>
        <span className="text-[16px] font-medium tabular-nums text-ink">{headerBalance}</span>
      </div>
      <div className="no-scrollbar -mx-4 flex gap-[10px] overflow-x-auto px-4">
        {sorted.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onCardClick?.(c.id)}
            className="shrink-0 cursor-pointer text-left"
          >
            <CardPreview card={c} crediting={!!pendingByCard[c.id]} />
          </button>
        ))}
        <button
          type="button"
          onClick={onAddCard}
          className="flex h-[174px] w-[275px] shrink-0 flex-col items-center justify-center gap-2.5 rounded-[20px] border-2 border-dashed border-black/15 bg-white/40 active:bg-black/[0.03]"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/[0.06] text-[28px] font-light leading-none text-ink">
            +
          </span>
          <span className="text-[15px] font-medium text-ink">{t('cards.addCard')}</span>
        </button>
      </div>
    </section>
  );
}
